import { Injectable, inject } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { UpdateOnLangChange } from '../../shared/decorators/update-on-lang-change';

export interface GameCategoryType {
  id: any;
  authOnly?: boolean;
  name?: string;
  slug?: string;
}

export interface GameAttrType {
  id: any;
  name: string;
  slug?: string;
  short?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GameCategoryAttrService {
  private _api = inject(CmsApiService);
  private _user = inject(UserService);


  /**
   * Game category list observable
   */
  private _categoryList$: ReplaySubject<Array<GameCategoryType>> = new ReplaySubject<Array<GameCategoryType>>(1);

  /**
   * Game category list
   */
  private _categoryList: Array<GameCategoryType> = [];

  /**
   * Game category list for filter
   */
  private _filterCategoryList: GameCategoryType[] = [];

  /**
   * Game attr list observable
   */
  private _attrList$: ReplaySubject<Array<GameAttrType>> = new ReplaySubject<Array<GameAttrType>>(1);

  /**
   * Game attr list
   */
  private _attrList: Array<GameAttrType> = [];

  /**
   * Game attr list for filter
   */
  private _filterAttrList: GameAttrType[] = [];


  /**
   * Game category & attr list observable
   */
  private _categoryAttrList$: ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }> = new ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }>(1);

  constructor() {
    this.updateCategoryAttrList().subscribe();
  }

  /**
   * Access to category list observable from outside
   */
  get categoryList$(): ReplaySubject<Array<GameCategoryType>> {
    return this._categoryList$;
  }

  /**
   * Access to category list from outside
   */
  get categoryList(): Array<GameCategoryType> {
    return this._categoryList;
  }

  /**
   * Access to filter category list from outside
   */
  public get filterCategoryList() {
    return this._filterCategoryList;
  }

  /**
   * Access to attr list observable from outside
   */
  get attrList$(): ReplaySubject<Array<GameAttrType>> {
    return this._attrList$;
  }

  /**
   * Access to attr list from outside
   */
  get attrList(): Array<GameAttrType> {
    return this._attrList;
  }

  /**
   * Access to filter attr list from outside
   */
  public get filterAttrList() {
    return this._filterAttrList;
  }

  /**
   * Access to category & attr list observable from outside
   */
  get categoryAttrList$(): ReplaySubject<{ attributeList: GameAttrType[], categoryList: GameCategoryType[] }> {
    return this._categoryAttrList$;
  }


  /**
   * Returns category by id
   *
   * @param id
   */
  getCategoryById(id): GameCategoryType {
    return this._categoryList.find(category => category.id === id.toString());
  }

  /**
   * Returns category by name
   *
   * @param name
   */
  getCategoryByAlias(name: string): GameCategoryType {
    name = name.toLowerCase().replace(' ', '-');
    return this._categoryList.find(category => category.name === name);
  }

  /**
   * Returns attr by id
   *
   * @param id
   */
  getAttrById(id): GameAttrType {
    return this._attrList.find(attr => attr.id === +id);
  }

  /**
   * Returns attr by name
   *
   * @param name
   */
  getAttrByAlias(name: string): GameAttrType {
    name = name.toLowerCase().replace(' ', '-');
    return this._attrList.find(attr => attr.slug === name);
  }


  /**
   * Get category & attr list from CMS and save to local variables
   */
  @UpdateOnLangChange('game/category/attribute/list')
  updateCategoryAttrList(): ReplaySubject<any> {
    let params = {};

    if (this._user.auth) {
      params = {
        country: this._user.info.country
      };
    }

    return this._api.gameCategoryAttrList(params).pipe(
      map(response => response.data),
      map((data: { categoryList: GameCategoryType[], attributeList: GameAttrType[] }) => {
        data.categoryList = data.categoryList.map(category => ({
          ...category,
          id: +category.id,
        })).map(category => {
          return category;
        });

        data.attributeList = data.attributeList.map(attr => ({
          ...attr,
          id: +attr.id,
          slug: attr.short,
        }));
        return data;
      }),
      tap((data: { categoryList: GameCategoryType[], attributeList: GameAttrType[] }) => {
        this._categoryList = data.categoryList;
        this._filterCategoryList = data.categoryList
          .sort((a, b) =>
            a.name.toUpperCase() < b.name.toUpperCase() ? -1 : a.name.toUpperCase() > b.name.toUpperCase() ? 1 : 0);
        this._attrList = data.attributeList;
        this._filterAttrList = data.attributeList
          .sort((a, b) =>
            a.name.toUpperCase() < b.name.toUpperCase() ? -1 : a.name.toUpperCase() > b.name.toUpperCase() ? 1 : 0);
        this._categoryList$.next(this._categoryList);
        this._attrList$.next(this._attrList);
        this._categoryAttrList$.next({attributeList: this._attrList, categoryList: this._categoryList});
      })
    ) as ReplaySubject<any>;
  }

}
